import { useParams } from 'react-router-dom'
import { useState, useEffect } from 'react'
import axios from 'axios'
import Header from '../components/shared/Header'
import Footer from '../components/shared/Footer'
import MacroSimulatorPage from './MacroSimulatorPage'
import MetaTags from '../components/shared/MetaTags'
import StructuredData from '../components/shared/StructuredData'
import ShareButtons from '../components/shared/ShareButtons'

export default function ExperimentDetailPage() {
  const { slug } = useParams()
  const [experiment, setExperiment] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showSimulator, setShowSimulator] = useState(false)
  const [showDevLog, setShowDevLog] = useState(false)

  useEffect(() => {
    const fetchExperiment = async () => {
      try {
        const response = await axios.get(`/api/v1/experiments/${slug}`)
        setExperiment(response.data)
      } catch (error) {
        console.error('Failed to fetch experiment:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchExperiment()
  }, [slug])

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-dark text-text-dark flex items-center justify-center">
        <div className="text-muted-dark">Loading experiment...</div>
      </div>
    )
  }

  if (!experiment) {
    return (
      <div className="min-h-screen bg-bg-dark text-text-dark">
        <div className="max-w-container mx-auto px-5 py-8">
          <Header />
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <h1 className="text-2xl mb-4">Experiment not found</h1>
              <button 
                onClick={() => window.location.href = '/'}
                className="text-accent-dark hover:underline"
              >
                Return home
              </button>
            </div>
          </div>
          <Footer />
        </div>
      </div>
    )
  }

  if (showSimulator) {
    return (
      <div className="relative">
        <div className="fixed top-4 left-4 z-[1000]">
          <button
            onClick={() => setShowSimulator(false)}
            className="px-4 py-2 bg-surface-dark-1 border border-border-dark rounded-lg text-text-dark hover:border-accent-dark transition-colors flex items-center gap-2"
          >
            ← Back to Experiment Details
          </button>
        </div>
        <MacroSimulatorPage />
      </div>
    )
  }

  const statusColor = experiment.status.state === 'iterating' 
    ? 'border-[rgba(122,215,255,0.28)] bg-accent-muted-dark text-text-dark'
    : 'border-border-dark text-muted-dark bg-white/[0.02]'

  return (
    <>
      {experiment && (
        <>
          <MetaTags
            title={`${experiment.title} - Experiment ${String(experiment.number).padStart(3, '0')} | deedslab.com`}
            description={experiment.description || "An experiment in testing ideas through real-world action and learning."}
            url={`/experiments/${experiment.slug}`}
            type="article"
          />
          <StructuredData
            type="Article"
            title={`${experiment.title} - Experiment ${String(experiment.number).padStart(3, '0')} | deedslab.com`}
            description={experiment.description || "An experiment in testing ideas through real-world action and learning."}
            url={`/experiments/${experiment.slug}`}
            datePublished={experiment.created_at}
            dateModified={experiment.updated_at}
            experimentData={experiment}
          />
        </>
      )}
      <div
        className="min-h-screen"
        style={{
          background: 'radial-gradient(900px 520px at 20% -10%, rgba(122, 215, 255, 0.18), transparent 58%)'
        }}
      >
      <div className="max-w-container mx-auto px-5 py-8">
        <Header />

        {/* Experiment Header */}
        <header className="mb-6 md:mb-8">
          <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4 flex-wrap">
            <span className="font-mono text-xs tracking-wide uppercase text-muted-dark">
              Experiment {String(experiment.number).padStart(3, '0')}
            </span>
            <span className={`font-mono text-[11px] tracking-wide uppercase py-1.5 px-2.5 rounded-full border ${statusColor}`}>
              {experiment.status.state.charAt(0).toUpperCase() + experiment.status.state.slice(1)}
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-semibold mb-3 md:mb-4 leading-tight tracking-tight">
            {experiment.name}
          </h1>
          <div className="flex items-center gap-3 md:gap-4 text-xs md:text-sm text-muted-dark font-mono mb-4 md:mb-6 flex-wrap">
            <span>Initiated {experiment.initiated}</span>
            {experiment.status.last_signal && (
              <>
                <span>•</span>
                <span>Last signal {experiment.status.last_signal}</span>
              </>
            )}
            {!experiment.status.last_signal && (
              <>
                <span>•</span>
                <span>Awaiting signal</span>
              </>
            )}
          </div>
          
          {/* Launch Simulator CTA - Moved to top */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
            <button
              onClick={() => setShowSimulator(true)}
              className="inline-flex items-center gap-2 sm:gap-3 px-4 sm:px-6 py-2.5 sm:py-3 bg-accent-dark text-black font-semibold text-sm sm:text-base rounded-lg hover:shadow-[0_8px_20px_rgba(122,215,255,0.6)] transition-all hover:scale-105 w-full sm:w-auto justify-center"
            >
              <span>Launch Simulator</span>
              <span className="text-lg sm:text-xl">→</span>
            </button>
            <span className="text-xs sm:text-sm text-muted-dark font-mono">
              Explore the particle-based visualization
            </span>
          </div>
        </header>

        {/* Share Buttons */}
        <div className="flex justify-center sm:justify-start mb-6 md:mb-8">
          <ShareButtons
            title={`${experiment.title} - Experiment ${String(experiment.number).padStart(3, '0')} | deedslab.com`}
            description={experiment.description || "An experiment in testing ideas through real-world action and learning."}
            url={`/experiments/${experiment.slug}`}
          />
        </div>

        {/* Experiment Phases */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-8 md:mb-12">
          {/* Ideating */}
          <div className="border border-border-dark rounded-large bg-white/[0.04] p-4 md:p-6">
            <div className="flex items-center gap-2 mb-4">
              <svg className={`w-5 h-5 ${experiment.status.state === 'ideating' ? 'text-accent-dark' : 'text-muted-dark'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <h3 className="font-mono text-xs tracking-wide uppercase text-muted-dark">Ideating</h3>
            </div>
            <div className="text-sm text-muted-dark leading-relaxed">
              Develop an intuitive explanation for why insurance premiums increase even when an individual 
              insured didn't take a loss. By visualizing the macro system—premium flows, loss events, 
              carrier margins, and reinsurance dynamics—we can show how pool-level performance drives 
              individual pricing.
            </div>
          </div>

          {/* Iterating */}
          <div className="border border-border-dark rounded-large bg-white/[0.04] p-4 md:p-6">
            <div className="flex items-center gap-2 mb-4">
              <svg className={`w-5 h-5 ${experiment.status.state === 'iterating' ? 'text-accent-dark' : 'text-muted-dark'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <h3 className="font-mono text-xs tracking-wide uppercase text-muted-dark">Iterating</h3>
            </div>
            <div className="text-sm text-muted-dark leading-relaxed">
              Building the simulation model with particle-based physics to explore interactions between market forces, loss events, and capital flows.
            </div>
          </div>

          {/* Learnings */}
          <div className="border border-border-dark rounded-large bg-white/[0.04] p-4 md:p-6">
            <div className="flex items-center gap-2 mb-4">
              <svg className={`w-5 h-5 ${experiment.status.state === 'learnings' ? 'text-accent-dark' : 'text-muted-dark'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="font-mono text-xs tracking-wide uppercase text-muted-dark">Learnings</h3>
            </div>
            <div className="text-sm text-muted-dark leading-relaxed">
              Insights will emerge as the model iterates. Initial focus on understanding feedback loops between premium flows and claim events.
            </div>
          </div>
        </div>

        {/* The Core Question */}
        <div className="border-2 border-accent-dark rounded-large bg-accent-muted-dark p-8 mb-8">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <svg className="w-10 h-10 text-accent-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-semibold mb-3">The Core Question</h2>
              <p className="text-lg leading-relaxed">
                <strong>"Why did my premium go up when I didn't even have a claim?"</strong>
              </p>
              <p className="text-muted-dark mt-3 leading-relaxed">
                This is one of the most common frustrations in insurance. You've been a perfect customer—no 
                accidents, no claims—yet your renewal notice shows a price increase. It feels unfair. 
                But the answer lies in understanding insurance at the macro level, not the individual level.
              </p>
            </div>
          </div>
        </div>

        {/* How This Simulator Explains It */}
        <div className="border border-border-dark rounded-large bg-white/[0.04] p-8 mb-8">
          <h2 className="text-2xl font-semibold mb-6">How This Simulator Explains It</h2>
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2 flex items-start gap-2">
                <span className="text-accent-dark flex-shrink-0">1.</span>
                <span>Insurance is a Pool, Not Individual Contracts</span>
              </h3>
              <p className="text-muted-dark leading-relaxed">
                When you pay your premium, that money goes into a large pool with thousands (or millions) 
                of other policyholders. The insurance carrier uses this pool to pay claims from ANYONE 
                in the pool—not just you. Your premium doesn't sit in a personal account waiting for your 
                claim. It's immediately part of a collective fund.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2 flex items-start gap-2">
                <span className="text-accent-dark flex-shrink-0">2.</span>
                <span>Pool Performance Drives Pricing</span>
              </h3>
              <p className="text-muted-dark leading-relaxed">
                If the pool experiences more claims than expected—hurricanes, wildfires, increased car 
                accidents—the carrier needs to collect more premium from EVERYONE to maintain solvency. 
                Even if you personally had zero claims, you're part of a pool that collectively had losses. 
                The macro performance determines your individual price.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2 flex items-start gap-2">
                <span className="text-accent-dark flex-shrink-0">3.</span>
                <span>Macro Forces Create Ripple Effects</span>
              </h3>
              <p className="text-muted-dark leading-relaxed">
                The simulation shows how loss events in one area flow through the entire system. When 
                carriers pay large claims, their profit margins shrink. To restore margins, they raise 
                rates across the pool. When reinsurers see heavy losses, they raise their prices to 
                carriers, who pass those costs to you. Individual behavior matters less than system-wide 
                dynamics.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2 flex items-start gap-2">
                <span className="text-accent-dark flex-shrink-0">4.</span>
                <span>What You'll See in the Simulator</span>
              </h3>
              <p className="text-muted-dark leading-relaxed">
                Watch premiums flow from people and businesses into agencies and carriers. Observe claim 
                events that drain carrier reserves. Notice how profit margins shrink and rates adjust 
                across the entire market—not just for those who had claims. See reinsurance step in for 
                large losses and how their capacity costs flow back to carriers and ultimately to you.
              </p>
            </div>
          </div>
        </div>

        {/* The Takeaway */}
        <div className="border border-border-dark rounded-large bg-white/[0.04] p-8 mb-8">
          <h2 className="text-2xl font-semibold mb-4">The Takeaway</h2>
          <p className="text-muted-dark leading-relaxed text-lg">
            Your premium isn't a reflection of your individual risk alone—it's a reflection of the 
            health and performance of the entire insurance pool you're part of. By visualizing the 
            macro system, this simulator helps explain why individual experience and pricing don't 
            always align.
          </p>
        </div>

        {/* Development Log */}
        <div className="border border-border-dark rounded-large bg-white/[0.04] overflow-hidden mb-8">
          <button
            onClick={() => setShowDevLog(!showDevLog)}
            className="w-full px-8 py-5 flex items-center justify-between hover:bg-white/[0.02] transition-colors"
          >
            <div className="flex items-center gap-3">
              <svg className="w-6 h-6 text-muted-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h2 className="text-xl font-semibold">Development Log</h2>
              <span className="font-mono text-xs tracking-wide uppercase text-muted-dark hidden md:inline">Scientific Record</span>
            </div>
            <span className="text-2xl text-muted-dark transform transition-transform" style={{ transform: showDevLog ? 'rotate(180deg)' : 'rotate(0deg)' }}>
              ▼
            </span>
          </button>
          
          {showDevLog && (
            <div className="px-8 pb-8">
              <div className="border-t border-border-dark pt-6">
                <p className="text-sm text-muted-dark mb-6 leading-relaxed">
                  This log tracks key developments, observations, and methodological changes during the experiment's evolution.
                </p>

                {/* Log Entry */}
                <div className="space-y-6">
                  {/* Entry 1 */}
                  <div className="border-l-2 border-accent-dark pl-6">
                    <div className="flex items-baseline gap-3 mb-2">
                      <span className="font-mono text-xs text-accent-dark">2025-12-19</span>
                      <span className="font-mono text-xs uppercase tracking-wide text-muted-dark">Phase: Restructure</span>
                    </div>
                    <h3 className="font-semibold mb-2">Project Architecture Migration</h3>
                    <div className="text-sm text-muted-dark leading-relaxed space-y-2">
                      <p>
                        <strong className="text-text-dark">Change:</strong> Migrated from vanilla JavaScript monolithic structure 
                        to React + Tailwind + FastAPI modular architecture.
                      </p>
                      <p>
                        <strong className="text-text-dark">Rationale:</strong> Enable rapid iteration on UI/UX for explaining 
                        complex concepts. Component-based architecture allows A/B testing different explanatory approaches.
                      </p>
                      <p>
                        <strong className="text-text-dark">Technical:</strong> Backend (FastAPI) now serves API endpoints for 
                        experiment metadata. Frontend (React SPA) handles presentation layer. Preserved all particle simulation 
                        logic from original implementation.
                      </p>
                      <p>
                        <strong className="text-text-dark">Status:</strong> Migration complete. System operational. 
                        Deployment via Nginx with SSL on production domain.
                      </p>
                    </div>
                  </div>

                  {/* Entry 2 */}
                  <div className="border-l-2 border-muted-dark pl-6">
                    <div className="flex items-baseline gap-3 mb-2">
                      <span className="font-mono text-xs text-muted-dark">2025-12-19</span>
                      <span className="font-mono text-xs uppercase tracking-wide text-muted-dark">Phase: Refinement</span>
                    </div>
                    <h3 className="font-semibold mb-2">Narrative Structure Implementation</h3>
                    <div className="text-sm text-muted-dark leading-relaxed space-y-2">
                      <p>
                        <strong className="text-text-dark">Change:</strong> Added experiment detail page with progressive 
                        disclosure pattern. Users now see context and explanation before interactive simulation.
                      </p>
                      <p>
                        <strong className="text-text-dark">Hypothesis:</strong> Educational framing before interaction will 
                        improve comprehension of macro vs. micro dynamics. Testing whether explicit question framing 
                        ("Why did my premium go up?") increases engagement.
                      </p>
                      <p>
                        <strong className="text-text-dark">Implementation:</strong> Four-part explanation structure: 
                        (1) Core question, (2) Pool dynamics, (3) Macro forces, (4) Expected observations. 
                        CTA positioned for immediate access while preserving educational context.
                      </p>
                    </div>
                  </div>

                  {/* Entry 3 */}
                  <div className="border-l-2 border-muted-dark pl-6">
                    <div className="flex items-baseline gap-3 mb-2">
                      <span className="font-mono text-xs text-muted-dark">2025-12-13</span>
                      <span className="font-mono text-xs uppercase tracking-wide text-muted-dark">Phase: Foundation</span>
                    </div>
                    <h3 className="font-semibold mb-2">Initial Particle Simulation Engine</h3>
                    <div className="text-sm text-muted-dark leading-relaxed space-y-2">
                      <p>
                        <strong className="text-text-dark">Implementation:</strong> Built 6-entity particle system representing 
                        insurance market participants (People, Businesses, Agencies, Carriers, Wholesalers, Reinsurance). 
                        Each entity rendered as canvas with physics-based particle movement.
                      </p>
                      <p>
                        <strong className="text-text-dark">Technical Approach:</strong> Vanilla JavaScript with modular 
                        components. Particle systems use simplified physics (velocity, collision, gravity). Premium flows 
                        represented as visual particle streams between entities.
                      </p>
                      <p>
                        <strong className="text-text-dark">Observation:</strong> Visual representation successfully demonstrates 
                        systemic flow of capital. Next phase requires integration with explanatory narrative to connect 
                        visual metaphor to real market behavior.
                      </p>
                    </div>
                  </div>

                  {/* Placeholder for future entries */}
                  <div className="border border-dashed border-border-dark rounded-lg p-4 text-center">
                    <p className="text-sm text-muted-dark font-mono">
                      Future entries will be added as the experiment progresses...
                    </p>
                  </div>
                </div>

                {/* Methodology Note */}
                <div className="mt-8 pt-6 border-t border-border-dark">
                  <h3 className="font-mono text-xs uppercase tracking-wide text-muted-dark mb-3">Methodology Note</h3>
                  <p className="text-sm text-muted-dark leading-relaxed">
                    This log follows a structured format: <strong className="text-text-dark">Change</strong> (what was done), 
                    <strong className="text-text-dark"> Rationale</strong> (why), <strong className="text-text-dark">Technical</strong> (how), 
                    and <strong className="text-text-dark">Status/Observation</strong> (outcome). Entries are dated and tagged by phase 
                    to track the experiment's evolution from ideation through iteration to learnings.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <Footer />
      </div>
    </div>
    </>
  )
}

